import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { knowledge } from '@cwos/api-client';
import {
  Button,
  FilteredEmpty,
  Pill,
  Refused,
  SuccessEmpty,
  TableCard,
  type Column,
} from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { LoadFailure } from '../LoadFailure.js';
import { ARTICLE_PUBLISH_KEY, ARTICLE_WRITE_KEY, ArticleDetail } from './ArticleDetail.js';
import { articleStatusLabel, articleStatusVariant, audienceSentence } from './articlesLogic.js';

/**
 * THE ARTICLES FACE OF THE LIBRARY — the policies a person writes, as opposed
 * to the documents an organization uploads and the rules approved out of them.
 *
 * These are ONE destination and not three, which is why this is a tab beside
 * Sources, Policies & rules and What applies rather than a screen of its own.
 * An operator asking "what does this organization say about supervision" should
 * not have to know whether the answer was typed into Phibian or extracted from
 * a handbook.
 *
 * WHAT A READER SEES DEPENDS ON WHETHER THEY AUTHOR, and the server decides it
 * — a plain `knowledge:read` holder (the worker role) is served only published
 * articles aimed at their programs and audience bands. The client never asks
 * for a scope and must not reproduce the predicate, which has already moved
 * once. So the empty state here says "none aimed at you", not "your
 * organization has none".
 *
 * A DETAIL OPENS IN PLACE. `/library/articles/$articleId` is a route this lane
 * cannot add — `App.tsx` has one owner — so the list holds the open article in
 * its own state and `ArticleDetailRoute` is exported ready for the route line
 * when the integrator applies it. Both render the same component.
 */
export function Articles() {
  const { has } = usePermissions();
  const mayWrite = has(ARTICLE_WRITE_KEY);
  const mayPublish = has(ARTICLE_PUBLISH_KEY);
  const [status, setStatus] = useState<knowledge.KnowledgeArticleStatus | ''>('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  const list = useQuery({
    queryKey: ['knowledge', 'articles', status],
    queryFn: () =>
      knowledge.articles(
        status === ''
          ? { includeArchived: true }
          : status === 'archived'
            ? { status, includeArchived: true }
            : { status },
      ),
  });

  const rows = list.data ?? [];

  if (openId !== null) {
    return <ArticleDetail articleId={openId} onBack={() => setOpenId(null)} />;
  }

  const columns: Column<knowledge.KnowledgeArticle>[] = [
    { key: 'title', header: 'Policy', render: (a) => <span className="rn-strong">{a.title}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (a) => (
        <Pill variant={articleStatusVariant(a.status)}>{articleStatusLabel(a.status)}</Pill>
      ),
    },
    { key: 'audience', header: 'Who it is for', render: (a) => audienceSentence(a) },
    {
      key: 'served',
      header: 'Being served',
      // `currentVersionId === null` is a REAL STATE: written and never
      // published, so no reader has ever been shown it. An empty cell would
      // read as missing data about a policy that is in force.
      render: (a) =>
        a.currentVersionId === null ? (
          <span className="rn-muted">No version published</span>
        ) : (
          'Yes'
        ),
    },
  ];

  return (
    <div className="rn-col">
      <div className="rn-row">
        <label>
          Status
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as knowledge.KnowledgeArticleStatus | '')}
          >
            <option value="">Every status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Withdrawn</option>
          </select>
        </label>
        {mayWrite ? (
          <Button variant="primary" small onClick={() => setComposing((c) => !c)}>
            {composing ? 'Cancel' : 'Write a policy'}
          </Button>
        ) : null}
      </div>

      {mayWrite && composing ? (
        <Compose
          mayPublish={mayPublish}
          onDone={(id) => {
            setComposing(false);
            setOpenId(id);
          }}
        />
      ) : null}

      <ListState
        phase={listPhase(list, list.data === undefined ? undefined : rows.length)}
        surface="table"
        rows={6}
        failedMessage="We couldn’t load your policies just now. This is not a statement that you have none."
        onRetry={() => void list.refetch()}
        refused={
          <Refused
            heading="Policies are not shown to you"
            message="Reading your organization’s written policies needs a permission you do not hold. It is withheld, not empty."
          />
        }
        empty={
          status === '' ? (
            <SuccessEmpty
              heading={mayWrite ? 'No policies yet' : 'Nothing aimed at you yet'}
              message={
                mayWrite
                  ? 'A policy written here is what your people read and acknowledge. Nobody sees one until a version is published.'
                  : 'Phibian serves you the published policies aimed at your programs and your role. There are none — which is not a statement that your organization has none.'
              }
            />
          ) : (
            <FilteredEmpty onClear={() => setStatus('')} />
          )
        }
      >
        <TableCard
          columns={columns}
          rows={rows}
          rowKey={(a) => a.id}
          onRowOpen={(a) => setOpenId(a.id)}
          caption="Written policies this organization keeps"
        />
      </ListState>
    </div>
  );
}

/**
 * Writing the first version of a policy.
 *
 * THE AUDIENCE BAND CANNOT BE A CLOSED LIST and that is why the warning below
 * exists: an organization invents its own role keys, so a mistyped or retired
 * band is a well-formed request that stores fine and reaches nobody. The server
 * re-reads the stored row after every authoring write and answers
 * `AUDIENCE_MATCHES_NOBODY`; an author cannot recompute that without the role
 * vocabulary, and the status word will never carry it. So it is rendered at the
 * point of the write, on the success.
 *
 * PROGRAM TARGETING IS NOT OFFERED HERE, and that is an absence rather than a
 * default: `programId` narrows an article to one program and choosing one needs
 * the program vocabulary this screen does not read. An article created here is
 * not narrowed by program, which is the widest setting — said out loud, because
 * an author who assumed otherwise would publish to more people than they meant.
 */
function Compose({ mayPublish, onDone }: { mayPublish: boolean; onDone: (id: string) => void }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('');
  const [warning, setWarning] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      knowledge.createArticle({
        title: title.trim(),
        body,
        ...(audience.trim() === '' ? {} : { audience: audience.trim() }),
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['knowledge', 'articles'] });
      if (result.audienceWarning !== null) {
        // DO NOT NAVIGATE PAST A WARNING. Opening the detail would replace the
        // one sentence that says the policy reaches nobody with a page that
        // says "Draft" — which is true and is not the thing the author needs
        // to read.
        setWarning(result.audienceWarning);
        return;
      }
      onDone(result.article.id);
    },
  });

  return (
    <form
      className="rn-card rn-col"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim() === '' || body.trim() === '') return;
        create.mutate();
      }}
    >
      <h2 className="rn-card-title">Write a policy</h2>
      <label className="rn-col">
        <span className="rn-strong">Title</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Policy title" />
      </label>
      <label className="rn-col">
        <span className="rn-strong">The policy</span>
        <textarea
          value={body}
          rows={10}
          onChange={(e) => setBody(e.target.value)}
          aria-label="The policy"
        />
      </label>
      <label className="rn-col">
        <span className="rn-strong">Whose role this is for</span>
        <input
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
          aria-label="Whose role this is for"
        />
        <span className="rn-muted">
          Leave this empty to reach everyone. If you name a role, it has to be a role your
          organization actually uses — Phibian checks after saving and tells you if it matches
          nobody.
        </span>
      </label>
      <p className="rn-muted">
        This will be a draft. It is not narrowed to a program, so once published it reaches everyone
        whose role matches.{' '}
        {mayPublish
          ? 'You can publish it from the policy itself.'
          : 'Publishing it needs a separate permission from writing it.'}
      </p>
      {warning ? (
        <p className="rn-strong" role="alert">
          {warning}
        </p>
      ) : null}
      <Button type="submit" variant="primary" disabled={create.isPending}>
        {create.isPending ? 'Saving…' : 'Save as a draft'}
      </Button>
      {create.isError ? (
        <LoadFailure
          error={create.error}
          message="We couldn’t save that policy just now. Nothing was written, so your words are still in the box."
          refusedHeading="You cannot write policies"
        />
      ) : null}
    </form>
  );
}
